import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, KeyRound, Trash2, Eye, EyeOff, RefreshCw } from "lucide-react";

const MCP_PAD = "/api/mcp";

export default function InstellingenClaude() {
  const [st, setSt] = useState(null);
  const [zichtbaar, setZichtbaar] = useState(false);
  const [log, setLog] = useState([]);
  const url = `${process.env.REACT_APP_BACKEND_URL}${MCP_PAD}`;

  const laad = async () => {
    const [{ data }, { data: l }] = await Promise.all([
      api.get("/mcp-beheer/status"),
      api.get("/mcp-beheer/log", { params: { limiet: 15 } }),
    ]);
    setSt(data);
    setLog(l || []);
  };

  useEffect(() => { laad().catch(() => toast.error("Laden mislukt")); }, []);

  const maak = async () => {
    if (st?.heeft_token && !window.confirm("Er is al een token. Een nieuwe maken betekent dat je hem opnieuw in Claude moet invullen. Doorgaan?")) return;
    const { data } = await api.post("/mcp-beheer/token");
    setZichtbaar(true);
    toast.success("Nieuwe connector-token aangemaakt");
    await laad();
    navigator.clipboard?.writeText(data.token).catch(() => {});
  };

  const intrekken = async () => {
    if (!window.confirm("Token intrekken? Claude kan dan niets meer uitlezen.")) return;
    await api.delete("/mcp-beheer/token");
    toast.success("Token ingetrokken");
    await laad();
  };

  const kopieer = (tekst, wat) => {
    navigator.clipboard?.writeText(tekst);
    toast.success(`${wat} gekopieerd`);
  };

  if (!st) return <div className="p-6 text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="instellingen-claude">
      <div>
        <h1 className="font-display text-3xl font-medium flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" /> Claude-connector
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Koppel dit CRM aan je eigen Claude-abonnement (Desktop of de app). Claude kan dan zelf je
          omzet, marges, voorraad, klanten, bol-cijfers en facturen opvragen — en een taak of
          campagne-concept voor je klaarzetten. Je gebruikt je eigen Claude-account, dus hier lopen
          geen kosten via het CRM.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Connector-token</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {st.heeft_token
                ? `Aangemaakt ${String(st.aangemaakt_op || "").slice(0, 16).replace("T", " ")} · ${st.aanroepen || 0} aanroepen`
                : "Nog geen token aangemaakt"}
              {st.laatst_gebruikt ? ` · laatst gebruikt ${String(st.laatst_gebruikt).slice(0, 16).replace("T", " ")}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={maak} data-testid="mcp-token-nieuw">
              <KeyRound className="h-3.5 w-3.5 mr-1" />{st.heeft_token ? "Nieuwe token" : "Token aanmaken"}
            </Button>
            {st.heeft_token && (
              <Button size="sm" variant="outline" className="text-destructive" onClick={intrekken}
                data-testid="mcp-token-intrekken">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={laad} data-testid="mcp-herlaad">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {st.heeft_token && (
          <div className="flex items-center gap-2">
            <Input readOnly value={zichtbaar ? st.token : "•".repeat(28)} className="font-mono text-xs"
              data-testid="mcp-token-veld" />
            <Button size="sm" variant="outline" onClick={() => setZichtbaar(v => !v)} data-testid="mcp-token-zichtbaar">
              {zichtbaar ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="outline" onClick={() => kopieer(st.token, "Token")} data-testid="mcp-token-kopieer">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="text-sm font-medium">Zo koppel je het in Claude</div>
        <ol className="text-sm space-y-3 list-decimal pl-5">
          <li>
            Open <strong>Claude Desktop</strong> (of claude.ai in de browser) → <strong>Instellingen</strong> →{" "}
            <strong>Connectors</strong> → <strong>Aangepaste connector toevoegen</strong>.
          </li>
          <li>
            Vul bij URL exact dit in:
            <div className="flex items-center gap-2 mt-1.5">
              <Input readOnly value={url} className="font-mono text-xs" data-testid="mcp-url" />
              <Button size="sm" variant="outline" onClick={() => kopieer(url, "URL")} data-testid="mcp-url-kopieer">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </li>
          <li>
            Open <strong>Geavanceerde instellingen</strong> → <strong>Request headers</strong> en voeg toe:
            header <code className="text-xs bg-secondary px-1 rounded">authorization</code> met als waarde{" "}
            <code className="text-xs bg-secondary px-1 rounded">Bearer {zichtbaar && st.token ? st.token : "<jouw token>"}</code>.
            {st.heeft_token && (
              <Button size="sm" variant="outline" className="ml-2" onClick={() => kopieer(`Bearer ${st.token}`, "Header-waarde")}
                data-testid="mcp-header-kopieer">
                <Copy className="h-3.5 w-3.5 mr-1" />Waarde kopiëren
              </Button>
            )}
          </li>
          <li>Klik op toevoegen. Claude haalt de tools op; zet in het gesprek de connector aan.</li>
          <li>
            In de <strong>mobiele app</strong> werkt dezelfde connector automatisch mee, zolang je hem in
            Claude Desktop/web hebt toegevoegd onder hetzelfde account.
          </li>
        </ol>
        <p className="text-xs text-muted-foreground">
          Let op: aangepaste connectors zijn beschikbaar op de betaalde Claude-abonnementen. De
          verbinding loopt over https en werkt alleen met deze token — trek hem in en maak een nieuwe
          als je twijfelt.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="text-sm font-medium mb-2">Wat Claude kan opvragen ({(st.tools || []).length} tools)</div>
        <div className="flex flex-wrap gap-1.5">
          {(st.tools || []).map(t => <Badge key={t} variant="secondary" className="font-mono text-[10px]">{t}</Badge>)}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Lezen: volledig CRM-overzicht, producten met voorraad en dekking, klantdossier met omzet en
          activiteiten, omzet per maand/klant/categorie/product en voorraadmutaties per product.
          Schrijven: alleen een taak of een campagne-concept aanmaken.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl" data-testid="mcp-log">
        <div className="p-4 border-b border-border text-sm font-medium">Laatste aanroepen door Claude</div>
        <div className="divide-y divide-border">
          {log.map(r => (
            <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-xs">
              <span className="font-mono">{r.tool}</span>
              <span className="text-muted-foreground truncate max-w-[45%]">
                {JSON.stringify(r.argumenten || {}).slice(0, 80)}
              </span>
              <span className="text-muted-foreground">{String(r.op || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
          {log.length === 0 && <p className="p-4 text-xs text-muted-foreground">Nog geen aanroepen.</p>}
        </div>
      </div>
    </div>
  );
}
